// @react
import React, { ReactElement } from 'react'
// @typings
import { CommunicationFormValues } from 'modules/common/forms/types/communication'
import { GetUserQueryResult } from 'lib/graphql/graphqlTypes'
// @libraries
import { Control, Controller, UseFormMethods, useWatch } from 'react-hook-form'
import { makeStyles } from '@material-ui/core/styles'
// @components
import Accordion from '@bit/atd.web.accordion'
import InputCheckbox from '@bit/atd.web.forms.input-checkbox'
import InputCheckboxGroup from '@bit/atd.web.forms.input-checkbox-group'

// @todo, we should move this style out of this repo
const useStyles = makeStyles(() => ({
  accordion: {
    '& .MuiAccordion-root': {
      borderWidth: '1px',
      '&:first-child': {
        borderTop: 'none',
        borderBottom: 'none',
      },
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    '& .MuiAccordion-root .MuiAccordionSummary-root': {
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingTop: '12px',
      paddingBottom: '24px',
    },
    '& .MuiAccordion-root .MuiAccordionSummary-root.Mui-expanded': {
      borderBottom: '1px solid #dbd9d6',
    },
    '& .MuiAccordion-root .MuiAccordionSummary-content > div': {
      width: '90%',
    },
    '& .MuiAccordionDetails-root': {
      padding: '16px 24px',
      justifyContent: 'center',
    },
    '& .MuiAccordionDetails-root > div': {
      maxWidth: '568px',
      width: '100%',
      display: 'flex',
      margin: '0 auto',
      '& > div': {
        width: '100%',
        paddingRight: '0',
      },
      '& legend': {
        display: 'none',
      },
      '& label': {
        width: '100%',
      },
      '& .MuiCheckbox-root + div': {
        flexGrow: 1,
      },
    },
    '& .MuiFormGroup-root > div': {
      marginTop: '0',
      paddingRight: '0',
    },
    '& + div': {
      // padding: '12px 24px 24px',
    },
  },
}))

type PropsType = {
  formMethods: UseFormMethods<CommunicationFormValues>
  control: Control<CommunicationFormValues>
  data?: GetUserQueryResult['data']
  isEditing: boolean
  watchFields: string[]
}

//
// Choose the e-mail updates to recieve
//

const EmailUpdateOptionsFields = ({
  formMethods,
  control,
  data,
  isEditing,
  watchFields,
}: PropsType): ReactElement => {
  // HOOKS
  const classes = useStyles()
  const watchSelections = useWatch<CommunicationFormValues>({
    control,
    name: watchFields,
  })

  // Remove Digest group from data
  const panelData = data?.user?.communicationPreferences.newsletters.filter(
    (x) => x.newsletterGroupLabel !== 'Digest',
  )

  // Generate panels from clean data
  const createPanels = panelData?.map((panel) => {
    let watchOptions: string[] = []
    const panelName = panel.newsletterGroupLabel
    const panelOptions = panel.newsletterGroup.map((x) => {
      return {
        label: x.label || '',
        checked: x.value || false,
        id: x.key,
      }
    })

    if (!panelName || !panelOptions)
      return {
        title: <></>,
        content: <></>,
      }

    Object.keys(watchSelections).map((item: string) => {
      const res = item.includes(panelName)
      if (res && watchSelections.hasOwnProperty(item)) {
        // @ts-ignore
        watchOptions = watchSelections[item]
      }
    })

    // const onChangeHandler = () => {}

    const description = (panelName: string) => {
      switch (panelName) {
        case 'ATD Newsletters':
          return 'Receive monthly e-newsletters on the topics and industries you choose.'
        case 'Opportunities and Offers':
          return 'Receive information about ATD conferences and events, education programs and certification, publications, webinars, and more.'
      }
      return `Receive monthly e-newsletters on the ${panelName} you choose.`
    }

    return {
      title: (
        <InputCheckbox
          stopPropagation
          disabled={!isEditing}
          name={panelName}
          label={panelName}
          labelPlacement="right"
          size="medium"
          indeterminate={
            watchOptions.length > 0 && watchOptions.length < panelOptions.length
          }
          checked={watchOptions.length === panelOptions.length}
          onChange={() => {
            const dataArray = panelOptions.map((x) => x.id)
            const dataCount = dataArray.length
            const currentCount = watchOptions?.length

            if (watchSelections.fewerEmails !== '') {
              formMethods.setValue('fewerEmails', '', { shouldDirty: true })
            }

            if (typeof currentCount === 'number') {
              if (currentCount === 0 || currentCount < dataCount) {
                return formMethods.setValue(panelName, dataArray, {
                  shouldDirty: true,
                })
              }
              if (currentCount === dataCount) {
                return formMethods.setValue(panelName, [], {
                  shouldDirty: true,
                })
              }
            }
          }}
          helperText={<span>{description(panelName)}</span>}
        />
      ),
      content: (
        <div>
          <Controller
            control={control}
            name={panelName}
            render={({ onBlur, value, name }) => (
              <InputCheckboxGroup
                disabled={!isEditing}
                name={name}
                layout="row"
                labelPlacement="left"
                size="small"
                value={value as string[]}
                options={panelOptions}
                onBlur={onBlur}
                onChange={(event) => {
                  if (watchSelections.fewerEmails !== '') {
                    return formMethods.setValue('fewerEmails', '')
                  }

                  const inputArray = value as string[]
                  const inputTarget = event.target as HTMLInputElement
                  let newValue: string[]

                  if (inputArray.includes(inputTarget.value)) {
                    newValue = inputArray.filter(
                      (value: string): boolean => value !== inputTarget.value,
                    )
                  } else {
                    newValue = [...inputArray, inputTarget.value]
                  }

                  return formMethods.setValue(panelName, newValue, {
                    shouldValidate: false,
                    shouldDirty: true,
                  })
                }}
              />
            )}
          />
        </div>
      ),
    }
  })

  return (
    <>
      {createPanels && (
        <Accordion className={classes.accordion} panels={createPanels} />
      )}
    </>
  )
}

export default EmailUpdateOptionsFields
